import React, { useEffect, useState, useRef, useMemo } from "react";
import { BsFillEmojiGrinFill, BsSearch, BsThreeDotsVertical, BsWhatsapp } from "react-icons/bs";
import StyledHeadingText from "../components/Fonts/StyledHeadingText";
import { getChatBetweenUsers, getChats, sendWhatsappMessage, sendWhatsappTemplateMessage } from "../services/whatsapp";
import { toast } from "react-toastify";
import userAvatar from '../assets/icons/user-avatar.png';
import { BiArrowBack, BiCheck, BiCheckDouble } from "react-icons/bi";
import { formatDate, formatDateTime } from "../utils/FormatDateTime";
import { sanitizeMobile } from "../utils/phone";
import CustomLoading from "../components/customLoading/CustomLoading";
import EmojiPicker from "emoji-picker-react";
import ShowModal from "../utils/ShowModal";
import { preProdToken, exportWhatsappMessagesUrl } from "../constants/apis";
import axios from "axios";
import { getUserRole } from "../helpers/local_storage_helpers";
import { IoMdAdd } from "react-icons/io";
import Dropdown from "../components/inputFields/DropDown";
import { Send, SendHorizonal, Paperclip, X, FileText, Image, Video, Music, Eye, Download, Calendar, Clock, Search } from "lucide-react";
import GalleryComponent from "../components/GalleryComponent";

// Function to apply WhatsApp-like formatting
const formatMessage = (text) => {
    if (!text) return "";

    return text
        .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
        .replace(/_(.*?)_/g, "<em>$1</em>")
        .replace(/~(.*?)~/g, "<del>$1</del>")
        .replace(/```(.*?)```/g, "<pre>$1</pre>")
        .replace(/`(.*?)`/g, "<code>$1</code>")
        .replace(
            /(https?:\/\/[^\s]+)/g,
            '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline">$1</a>'
        );
};

// Function to group messages by date
const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach((message) => {
        const date = formatDate(message.created_at);
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(message);
    });
    return groups;
};

// Function to format contact display
const formatContactDisplay = (contact) => {
    const name = contact.name;
    const contactNumber = contact.contact;
    const type = contact.type;

    const displayName = name || contactNumber;
    const displayType = type || "Unknown";

    return `${displayName} (${displayType})`;
};

// ─── Material Analog Clock Time Picker ────────────────────────────────────────
const ClockTimePicker = ({ value, onChange }) => {
    const parse = (v) => { const [hh, mm] = (v || '00:00').split(':').map(Number); return [hh || 0, mm || 0]; };
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('hour');
    const [selH, setSelH] = useState(() => parse(value)[0]);
    const [selM, setSelM] = useState(() => parse(value)[1]);
    const [dragging, setDragging] = useState(false);
    const svgRef = useRef(null);

    // Sync state when value prop changes (e.g. preset chips)
    useEffect(() => { const [hh, mm] = parse(value); setSelH(hh); setSelM(mm); }, [value]);

    const isPM  = selH >= 12;
    const h12   = selH % 12 || 12;

    const SIZE  = 256;
    const C     = SIZE / 2;  // 128
    const R     = 96;        // radius of numbers

    // (x, y) for item i of total placed around clock
    const clockPos = (i, total, r = R) => {
        const a = (i / total) * 2 * Math.PI - Math.PI / 2;
        return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
    };

    // Hand end-point
    const handAngle = mode === 'hour'
        ? ((h12 % 12) / 12) * 2 * Math.PI - Math.PI / 2
        : (selM / 60) * 2 * Math.PI - Math.PI / 2;
    const handX = C + R * Math.cos(handAngle);
    const handY = C + R * Math.sin(handAngle);

    // Decode angle → hour or minute
    const angleToVal = (clientX, clientY) => {
        const rect = svgRef.current.getBoundingClientRect();
        let angle = Math.atan2(clientY - rect.top - C, clientX - rect.left - C) * 180 / Math.PI + 90;
        if (angle < 0) angle += 360;
        if (mode === 'hour') {
            const h = Math.round(angle / 30) % 12;   // 0-11
            return isPM ? (h === 0 ? 12 : h + 12) : (h === 0 ? 0 : h);
        }
        return Math.round(angle / 6) % 60;
    };

    const onDown = (e) => { e.preventDefault(); setDragging(true); apply(e); };
    const onMove = (e) => { if (dragging) apply(e); };
    const onUp   = (e) => {
        if (!dragging) return;
        apply(e);
        setDragging(false);
        if (mode === 'hour') setTimeout(() => setMode('minute'), 180);
    };
    const apply  = (e) => {
        const v = angleToVal(e.clientX, e.clientY);
        mode === 'hour' ? setSelH(v) : setSelM(v);
    };

    const togglePM = (pm) => {
        if (pm  && selH < 12)  setSelH(selH + 12);
        if (!pm && selH >= 12) setSelH(selH - 12);
    };

    const confirm = () => {
        onChange(`${String(selH).padStart(2,'0')}:${String(selM).padStart(2,'0')}`);
        setOpen(false); setMode('hour');
    };
    const cancel = () => {
        const [hh, mm] = parse(value); setSelH(hh); setSelM(mm);
        setOpen(false); setMode('hour');
    };

    const hourItems   = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    const minuteItems = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

    const selHour = (hv) => h12 === hv;
    const selMin  = (mv) => (Math.round(selM / 5) * 5) % 60 === mv;

    return (
        <div>
            {/* ── Trigger button ── */}
            <button
                type="button"
                onClick={() => { setOpen(true); setMode('hour'); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 border border-gray-200 bg-gray-50 hover:bg-white rounded-xl text-sm font-medium text-gray-700 transition-all"
            >
                <Clock size={14} className="text-gray-400" />
                <span className="font-mono tracking-wider">
                    {String(h12).padStart(2,'0')}:{String(selM).padStart(2,'0')}
                    <span className="ml-1.5 text-xs text-gray-400">{isPM ? 'PM' : 'AM'}</span>
                </span>
            </button>

            {/* ── Full-screen overlay ── */}
            {open && (
                <div
                    className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
                    onMouseUp={onUp}
                    onMouseMove={onMove}
                    onClick={(e) => { if (e.target === e.currentTarget) cancel(); }}
                >
                    <div
                        className="bg-gray-900 rounded-3xl shadow-2xl overflow-hidden select-none"
                        style={{ width: '300px', animation: 'clockIn 0.2s ease-out' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="px-6 pt-5 pb-4 bg-gray-800">
                            <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase mb-3">Select Time</p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setMode('hour')}
                                    className={`text-5xl font-bold font-mono leading-none transition-colors ${
                                        mode === 'hour' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                                    }`}
                                >{String(h12).padStart(2,'00')}</button>
                                <span className="text-5xl font-bold text-gray-500 leading-none">:</span>
                                <button
                                    onClick={() => setMode('minute')}
                                    className={`text-5xl font-bold font-mono leading-none transition-colors ${
                                        mode === 'minute' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                                    }`}
                                >{String(selM).padStart(2,'0')}</button>
                                <div className="flex flex-col ml-3 gap-1">
                                    <button onClick={() => togglePM(false)}
                                        className={`text-sm font-bold leading-none transition-colors ${
                                            !isPM ? 'text-blue-400' : 'text-gray-500 hover:text-gray-300'
                                        }`}>AM</button>
                                    <button onClick={() => togglePM(true)}
                                        className={`text-sm font-bold leading-none transition-colors ${
                                            isPM ? 'text-blue-400' : 'text-gray-500 hover:text-gray-300'
                                        }`}>PM</button>
                                </div>
                            </div>
                        </div>

                        {/* Clock face */}
                        <div className="flex justify-center px-5 py-5 bg-gray-900">
                            <svg
                                ref={svgRef}
                                width={SIZE} height={SIZE}
                                className="cursor-pointer"
                                onMouseDown={onDown}
                            >
                                {/* Face background */}
                                <circle cx={C} cy={C} r={C - 2} fill="#374151" />

                                {/* Hand */}
                                <line x1={C} y1={C} x2={handX} y2={handY}
                                    stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" />

                                {/* Center dot */}
                                <circle cx={C} cy={C} r={5} fill="#60a5fa" />

                                {/* Hand tip circle */}
                                <circle cx={handX} cy={handY} r={20} fill="#60a5fa" opacity="0.95" />

                                {/* Hour numbers */}
                                {mode === 'hour' && hourItems.map((hv, i) => {
                                    const p = clockPos(i, 12);
                                    const sel = selHour(hv);
                                    return (
                                        <text key={hv} x={p.x} y={p.y}
                                            textAnchor="middle" dominantBaseline="central"
                                            fontSize="15" fontWeight={sel ? '700' : '400'}
                                            fill={sel ? '#111827' : '#d1d5db'}
                                        >{hv}</text>
                                    );
                                })}

                                {/* Minute numbers (every 5 min) */}
                                {mode === 'minute' && minuteItems.map((mv, i) => {
                                    const p = clockPos(i, 12);
                                    const sel = selMin(mv);
                                    return (
                                        <text key={mv} x={p.x} y={p.y}
                                            textAnchor="middle" dominantBaseline="central"
                                            fontSize="14" fontWeight={sel ? '700' : '400'}
                                            fill={sel ? '#111827' : '#d1d5db'}
                                        >{String(mv).padStart(2,'0')}</text>
                                    );
                                })}
                            </svg>
                        </div>

                        {/* Mode indicator dots */}
                        <div className="flex justify-center gap-2 pb-1">
                            <div className={`w-1.5 h-1.5 rounded-full transition-colors ${ mode === 'hour' ? 'bg-blue-400' : 'bg-gray-600' }`} />
                            <div className={`w-1.5 h-1.5 rounded-full transition-colors ${ mode === 'minute' ? 'bg-blue-400' : 'bg-gray-600' }`} />
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-1 px-5 pb-4 bg-gray-900">
                            <button onClick={cancel}
                                className="px-5 py-2 text-blue-400 text-sm font-bold uppercase tracking-wider hover:text-blue-300 transition-colors"
                            >Cancel</button>
                            <button onClick={confirm}
                                className="px-5 py-2 text-blue-400 text-sm font-bold uppercase tracking-wider hover:text-blue-300 transition-colors"
                            >OK</button>
                        </div>
                    </div>

                    <style>{`
                        @keyframes clockIn {
                            from { opacity: 0; transform: scale(0.92); }
                            to   { opacity: 1; transform: scale(1); }
                        }
                    `}</style>
                </div>
            )}
        </div>
    );
};
// ──────────────────────────────────────────────────────────────────────────────

function Whatsapp() {
    const [contactsData, setContactsData] = useState({
        results: [],
        next: null,
        previous: null,
        count: 0
    });
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [showAddNew, setShowAddNew] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingChats, setLoadingChats] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [newContact, setNewContact] = useState("");
    const [newContactTemplate, setNewContactTemplate] = useState("fasalam_app_update");
    const [showEmogies, setShowEmogies] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        contact: null
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Export modal state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [activePreset, setActivePreset] = useState(null);
    const [exportForm, setExportForm] = useState({
        startDate: "",
        startTime: "00:00",
        endDate: "",
        endTime: "23:59",
        message_keyword: ""
    });

    // Build ISO strings for the API from split date+time fields
    const buildExportPayload = () => ({
        start: exportForm.startDate ? `${exportForm.startDate}T${exportForm.startTime}:00` : "",
        end: exportForm.endDate ? `${exportForm.endDate}T${exportForm.endTime}:59` : "",
        message_keyword: exportForm.message_keyword
    });

    const applyPreset = (preset) => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        setActivePreset(preset);
        if (preset === "today") {
            const t = fmt(now);
            setExportForm(p => ({ ...p, startDate: t, startTime: "00:00", endDate: t, endTime: "23:59" }));
        } else if (preset === "yesterday") {
            const y = new Date(now); y.setDate(now.getDate() - 1);
            const t = fmt(y);
            setExportForm(p => ({ ...p, startDate: t, startTime: "00:00", endDate: t, endTime: "23:59" }));
        } else if (preset === "last7") {
            const s = new Date(now); s.setDate(now.getDate() - 6);
            setExportForm(p => ({ ...p, startDate: fmt(s), startTime: "00:00", endDate: fmt(now), endTime: "23:59" }));
        } else if (preset === "thisMonth") {
            const s = new Date(now.getFullYear(), now.getMonth(), 1);
            setExportForm(p => ({ ...p, startDate: fmt(s), startTime: "00:00", endDate: fmt(now), endTime: "23:59" }));
        }
    };

    const [templates, setTemplates] = useState([
        { label: "App Update", value: "fasalam_app_update" },
    ]);

    // Add refs
    const messagesEndRef = useRef(null);
    const contactsListRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    // Memoize formatted messages
    const formattedMessages = useMemo(() =>
        messages.map(msg => ({
            ...msg,
            formattedText: formatMessage(msg.message_text)
        })),
        [messages]
    );

    // Group messages by date
    const groupedMessages = useMemo(() =>
        groupMessagesByDate(formattedMessages),
        [formattedMessages]
    );

    // Extract all images for GalleryComponent
    const chatImages = useMemo(() => {
        const images = [];
        messages.forEach(msg => {
            if (msg.media_file) {
                const ext = msg.media_file.split('.').pop()?.toLowerCase();
                if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
                    images.push({
                        image: msg.media_file,
                        created_at: msg.created_at,
                        id: msg.id
                    });
                }
            }
            if (msg.message_text?.includes('Template') && msg.message_text.includes('image:')) {
                const imageUrl = msg.message_text.split('image: ')[1];
                if (imageUrl) {
                    images.push({
                        image: imageUrl,
                        created_at: msg.created_at,
                        id: msg.id
                    });
                }
            }
        });
        return images;
    }, [messages]);

    // Function to scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Auto-scroll when messages change or new chat is opened
    useEffect(() => {
        scrollToBottom();
    }, [messages, activeChat]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [message]);

    // Check message status periodically
    useEffect(() => {
        const interval = setInterval(() => {
            if (messages.some(msg => msg.sent && msg.status !== 'read') && activeChat) {
                fetchChats(activeChat.contact);
            }
        }, 50000);
        return () => clearInterval(interval);
    }, [messages, activeChat]);

    // Save message draft
    useEffect(() => {
        if (activeChat) {
            localStorage.setItem(`draft-${activeChat.contact}`, message);
        }
    }, [message, activeChat]);

    // Load message draft
    useEffect(() => {
        if (activeChat) {
            const draft = localStorage.getItem(`draft-${activeChat.contact}`);
            if (draft) setMessage(draft);
        }
    }, [activeChat]);

    // Handle typing indicator
    useEffect(() => {
        const timer = setTimeout(() => setIsTyping(false), 1000);
        return () => clearTimeout(timer);
    }, [message]);

    const cancelToken = useRef(null);

    // function to handle search
    const handleSearch = useMemo(() => {
        let timeoutId = null;

        return (query) => {
            setSearchQuery(query);

            // Clear previous timeout
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            // Cancel previous request if it exists
            if (cancelToken.current) {
                cancelToken.current.cancel("Operation canceled due to new request");
            }

            // Create new cancel token
            cancelToken.current = axios.CancelToken.source();

            // Set new timeout for debouncing
            timeoutId = setTimeout(() => {
                fetchData(true, query);
            }, 300); // 300ms delay
        };
    }, []);

    // Add ESC key handler to close chat
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && activeChat) {
                setActiveChat(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeChat]);

    // Handle scroll for pagination
    const handleScroll = () => {
        if (contactsListRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = contactsListRef.current;
            const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;

            if (isNearBottom && contactsData.next && !loadingMore) {
                loadMoreContacts();
            }
        }
    };

    // Load more contacts for pagination
    const loadMoreContacts = async () => {
        if (!contactsData.next) return;

        setLoadingMore(true);
        try {
            const response = await fetch(contactsData.next.replace("http://", "https://"), {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Token ${preProdToken}`,
                },
            });
            const data = await response.json();

            setContactsData(prev => ({
                ...data,
                results: [...prev.results, ...data.results]
            }));
        } catch (error) {
            console.error("Error loading more contacts:", error);
            toast.error("Failed to load more contacts");
        } finally {
            setLoadingMore(false);
        }
    };

    const startNewChat = async () => {
        if (!newContact.trim()) return;

        const phoneNumber = newContact.replace(/\D/g, '');
        if (phoneNumber.length < 10) {
            toast.error("Please enter a valid phone number");
            return;
        }

        const newChatContact = {
            to: phoneNumber,
            template_name: newContactTemplate,
        };
        try {
            // setLoading(true);
            let res = await sendWhatsappTemplateMessage(newChatContact);
            if (res.status === 200) {
                setMessages([]);
                setNewContact("");
                setNewContactTemplate("");
                setShowAddNew(false);
                toast.success("Message sent successfully");
            } else {
                toast.error(res.data?.message || "Unable to send Message");
            }
        } catch (error) {
            console.error("Error sending Message:", error);
            toast.error(error.response?.data?.message || "Failed to send message");
        }
    };

    const sendMessage = async () => {
        if (message.trim() || selectedFile) {
            let newMessage = new FormData();
            newMessage.append('to', activeChat.contact);
            newMessage.append('message', message || '');

            if (selectedFile) {
                newMessage.append('file', selectedFile);
                newMessage.append('media_type', getMediaType(selectedFile.type));
            }

            try {
                // setLoading(true);
                let res = await sendWhatsappMessage(newMessage);
                if (res.status === 200) {
                    fetchChats(activeChat.contact);
                    setMessage("");
                    setSelectedFile(null);
                } else {
                    toast.error(res.data?.message || "Unable to send Message");
                }
            } catch (error) {
                console.error("Error sending Message:", error);
                toast.error(error.response?.data?.message || "Failed to send message");
            }
        }
    };

    const fetchData = async (needLoading = true, search = "") => {
        if (needLoading) {
            setLoading(true);
        }
        try {
            let res = await getChats({
                search: search
            }, {
                cancelToken: cancelToken.current?.token
            });

            if (res.status === 200) {
                const data = res.data;
                setContactsData(data);
            } else {
                toast.error(res.data?.message || "Unable to fetch Data");
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error fetching Contacts:", error);
                toast.error(error.response?.data?.message || "Failed to fetch contacts");
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchChats = async (phone) => {
        setLoadingChats(true);
        // fetchData(false, searchQuery);
        try {
            let res = await getChatBetweenUsers(phone);
            if (res.status === 200) {
                const data = res.data;
                setMessages(data);
            } else {
                toast.error(res.data?.message || "Unable to fetch Chats");
            }
        } catch (error) {
            console.error("Error fetching Chats:", error);
            toast.error(error.response?.data?.message || "Failed to fetch chats");
        } finally {
            setLoadingChats(false);
        }
    };

    const handleActiveChat = async (contact) => {
        setActiveChat(contact);
        fetchChats(contact.contact);
        if (getUserRole() === "staff") {
            contactsData.results.map((item, index) => {
                if (item.contact === contact.contact) {
                    item.is_opened = true;
                }
            });
            setContactsData({ ...contactsData, results: contactsData.results });
        }
    };

    const handleToggleEmogies = async () => {
        setShowEmogies(!showEmogies);
    };

    const handleContextMenu = (e, contact) => {
        e.preventDefault();
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            contact
        });
    };

    const handleContextAction = (action) => {
        switch (action) {
            case 'unread':
                toast.info(`Marked ${contextMenu.contact.contact} as unread`);
                break;
        }
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleCloseContextMenu = () => {
        setContextMenu({ ...contextMenu, visible: false });
    };

    const didFetchRef = useRef(false);
    useEffect(() => {
        if (!didFetchRef.current) {
            fetchData();
            didFetchRef.current = true;
        }
        document.addEventListener('click', handleCloseContextMenu);
        return () => {
            document.removeEventListener('click', handleCloseContextMenu);
        };
    }, []);

    const toggleSetAddNew = () => {
        setShowAddNew(!showAddNew);
    };

    const handleExportMessages = async () => {
        const payload = buildExportPayload();
        if (!payload.start || !payload.end) {
            toast.error("Please select start and end date");
            return;
        }
        setExportLoading(true);
        try {
            const formData = new FormData();
            formData.append("start", payload.start);
            formData.append("end", payload.end);
            if (payload.message_keyword.trim()) {
                formData.append("message_keyword", payload.message_keyword.trim());
            }

            const response = await axios.post(exportWhatsappMessagesUrl, formData, {
                headers: {
                    Authorization: `Token ${preProdToken}`,
                },
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const contentDisposition = response.headers["content-disposition"];
            let filename = `whatsapp_messages_${payload.start.split("T")[0]}_to_${payload.end.split("T")[0]}.xlsx`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+?)"?($|;)/);
                if (match) filename = match[1];
            }
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success("Messages exported successfully!");
            setShowExportModal(false);
        } catch (error) {
            console.error("Export error:", error);
            toast.error(error.response?.data?.message || "Failed to export messages");
        } finally {
            setExportLoading(false);
        }
    };

    const onEmogiesChange = (emogi) => {
        const newMessage = message + emogi.emoji;
        setMessage(newMessage);
    };

    // File handling functions
    const handleFileSelect = (file) => {
        // Validate file size (max 16MB)
        if (file.size > 16 * 1024 * 1024) {
            toast.error("File size must be less than 16MB");
            return;
        }

        // Validate file type
        const allowedTypes = [
            'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
            'video/mp4', 'video/avi', 'video/mov', 'video/wmv',
            'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/aac',
            'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain', 'application/rtf'
        ];

        if (!allowedTypes.includes(file.type)) {
            toast.error("File type not supported. Please select an image, video, audio, or document file.");
            return;
        }

        setSelectedFile(file);
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const removeSelectedFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType.startsWith('image/')) return <Image className="w-5 h-5" />;
        if (fileType.startsWith('video/')) return <Video className="w-5 h-5" />;
        if (fileType.startsWith('audio/')) return <Music className="w-5 h-5" />;
        return <FileText className="w-5 h-5" />;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getMediaType = (mimeType) => {
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('video/')) return 'video';
        if (mimeType.startsWith('audio/')) return 'audio';
        return 'document'; // Default for all other file types (PDF, DOC, TXT, etc.)
    };

    // Extract YouTube ID
    const getYouTubeId = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    // Format message text (for links and line breaks)
    const formatMessageText = (text) => {
        if (!text) return '';

        // Convert URLs to clickable links
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const withLinks = text.replace(urlRegex, url =>
            `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${url}</a>`
        );

        // Convert newlines to <br>
        return withLinks.replace(/\n/g, '<br>');
    };

    return (
        <div className={`h-full flex bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-lg border border-gray-200 my-auto overflow-hidden`}>
            {/* Contacts Sidebar - Always visible */}
            <div className={`w-full md:w-1/3 lg:w-1/4 bg-white/95 backdrop-blur-sm border-r border-gray-200/60 flex flex-col ${activeChat ? 'hidden md:flex' : 'flex'}`}>
                {/* Sidebar Header */}
                <div className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white border-b border-green-600/20 flex justify-between items-center sticky top-0 z-20 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-full bg-white/20 border-2 border-white/30 overflow-hidden ring-2 ring-white/20">
                            <img src={userAvatar} alt="Profile" className="h-full w-full object-cover" />
                        </div>
                        <h2 className="font-bold text-lg text-white">Chats</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            className="p-2 rounded-full hover:bg-white/20 transition-colors"
                            onClick={() => setShowExportModal(true)}
                            title="Export WhatsApp Messages"
                        >
                            <Download size={18} className="text-white" />
                        </button>
                        <button className="p-2 rounded-full hover:bg-white/20 transition-colors">
                            <BsThreeDotsVertical size={18} className="text-white" />
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="p-3 bg-white/80 backdrop-blur-sm border-b border-gray-200/60 sticky top-[72px] z-10 shadow-sm">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <BsSearch className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search or start new chat"
                            className="w-full pl-11 pr-4 py-3 bg-gray-50/80 border border-gray-200/50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:bg-white focus:border-green-300 transition-all duration-200 placeholder-gray-400"
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>
                    {
                        showAddNew && (
                            <div className="mt-3 p-3 bg-green-50/50 rounded-xl border border-green-100/50 backdrop-blur-sm">
                                <input
                                    type="text"
                                    placeholder="Enter phone number"
                                    className="w-full px-4 py-3 bg-white/80 border border-gray-200/50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:bg-white focus:border-green-300 transition-all duration-200 placeholder-gray-400"
                                    value={newContact}
                                    onChange={(e) => setNewContact(sanitizeMobile(e.target.value))}
                                />
                                <div className="flex flex-row mt-3 gap-3 items-center">
                                    <div className="w-full flex-1">
                                        <Dropdown
                                            options={templates}
                                            value={newContactTemplate}
                                            onChange={(e) => setNewContactTemplate(e)}
                                            placeholder="Select Template"
                                        />
                                    </div>
                                    <button
                                        onClick={startNewChat}
                                        className="flex items-center justify-center p-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-xl shadow-lg text-white transition-all duration-200 hover:scale-105"
                                    >
                                        <SendHorizonal size={20} />
                                    </button>
                                </div>
                            </div>
                        )
                    }
                </div>

                {/* Contact List */}
                <div className="flex-1 overflow-y-auto relative" ref={contactsListRef} onScroll={handleScroll}>
                    {loading ? (
                        <div className="flex justify-center items-center h-20">
                            <CustomLoading />
                        </div>
                    ) : contactsData.results.length > 0 ? (
                        contactsData.results.map((contact) => (
                            <div
                                key={contact.whatsapp_message_id}
                                className={`flex items-center p-3 border-b border-gray-100 cursor-pointer transition-colors duration-150 border-l-4 ${activeChat?.contact === contact.contact
                                    ? 'bg-green-50/70 border-l-green-500'
                                    : 'border-l-transparent hover:bg-gray-50'
                                    }`}
                                onClick={() => handleActiveChat(contact)}
                            >
                                <div className="relative mr-3">
                                    <div className="h-12 w-12 rounded-full bg-gray-200 overflow-hidden ring-2 ring-white shadow-sm">
                                        <img src={userAvatar} alt={contact.contact} className="h-full w-full object-cover" />
                                    </div>
                                    {!contact.is_opened && (
                                        <div className="absolute -top-1 -right-1 h-4 w-4 bg-green-500 rounded-full border-2 border-white"></div>
                                    )}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-center">
                                        <h3 className="font-medium text-gray-900 truncate">{formatContactDisplay(contact)}</h3>
                                        <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                                            {formatDate(contact.updated_at)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <p className="text-sm text-gray-500 truncate pr-2">
                                            {contact.message_text || 'No messages yet'}
                                        </p>
                                        <div className="flex items-center">
                                            {contact.status === "read" ? (
                                                <BiCheckDouble className="text-blue-500" size={16} />
                                            ) : contact.status === "received" ? (
                                                <BiCheckDouble className="text-gray-400" size={16} />
                                            ) : (
                                                <BiCheck className="text-gray-400" size={16} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
                            <BsWhatsapp size={48} className="mb-4 text-gray-300" />
                            <p>No chats found</p>
                        </div>
                    )}
                    {loadingMore && (
                        <div className="flex justify-center p-3">
                            <CustomLoading size="small" />
                        </div>
                    )}
                </div>

                <div className="absolute bottom-24 right-6 z-10">
                    <button
                        onClick={toggleSetAddNew}
                        className="group flex items-center justify-center w-14 h-14 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-full shadow-2xl text-white transition-all duration-300 hover:scale-110 hover:rotate-90"
                    >
                        <IoMdAdd size={26} className="transition-transform duration-300 group-hover:scale-110" />
                    </button>
                </div>
            </div>

            {/* Chat Area - Hidden on mobile when no chat selected */}
            <div className={`flex-1 flex flex-col ${!activeChat ? 'hidden md:flex' : 'flex'}`}>
                {activeChat ? (
                    <>
                        {/* Chat Header */}
                        <div className="p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200/60 flex items-center sticky top-0 z-10 shadow-sm">
                            <button
                                className="md:hidden mr-3 p-2 rounded-full hover:bg-gray-100/80 transition-colors duration-200"
                                onClick={() => setActiveChat(null)}
                            >
                                <BiArrowBack size={22} className="text-gray-600" />
                            </button>
                            <div className="flex items-center gap-4 flex-1">
                                <div className="relative">
                                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden ring-2 ring-white shadow-lg transition-transform hover:scale-105">
                                        <img src={userAvatar} alt={activeChat.contact} className="h-full w-full object-cover" />
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 h-4 w-4 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-gray-800 text-lg">{formatContactDisplay(activeChat)}</h3>
                                    {isTyping ? (
                                        <div className="flex items-center gap-1">
                                            <div className="flex space-x-1">
                                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce"></div>
                                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                            </div>
                                            <span className="text-sm text-green-600 ml-2 font-medium">typing...</span>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500">Online</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button className="p-2 rounded-full hover:bg-gray-100/80 transition-colors duration-200">
                                        <BsSearch size={18} className="text-gray-600" />
                                    </button>
                                    <button className="p-2 rounded-full hover:bg-gray-100/80 transition-colors duration-200">
                                        <BsThreeDotsVertical size={18} className="text-gray-600" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Messages Area */}
                        {loadingChats ? (
                            <div className="flex-1 flex items-center justify-center">
                                <CustomLoading />
                            </div>
                        ) : (
                            <div
                                className={`flex-1 overflow-y-auto p-4 bg-[#e5ddd5] bg-opacity-30 transition-colors duration-200 ${isDragOver ? 'bg-green-100/50 border-2 border-dashed border-green-400' : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                {Object.entries(groupedMessages).map(([date, messages]) => (
                                    <div key={date}>
                                        <div className="flex items-center my-3 text-gray-500">
                                            <div className="flex-1 border-t border-gray-200"></div>
                                            <span className="mx-3 bg-white px-3 py-1 rounded-full text-xs shadow-sm">
                                                {date}
                                            </span>
                                            <div className="flex-1 border-t border-gray-200"></div>
                                        </div>
                                        {messages.map((msg, index) => {
                                            // Extract YouTube URL if exists
                                            const youtubeUrl = msg.message_text?.match(/(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/)?.[0];
                                            const youtubeId = youtubeUrl ? getYouTubeId(youtubeUrl) : null;

                                            return (
                                                <div
                                                    key={`${msg.id}-${index}`}
                                                    className={`mb-3 flex ${msg.sent ? "justify-end" : "justify-start"}`}
                                                >
                                                    <div
                                                        className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl relative transition-shadow duration-200 ${msg.sent
                                                            ? "bg-gradient-to-r from-green-500 to-green-600 text-white rounded-tr-md shadow-md hover:shadow-lg"
                                                            : "bg-white/90 backdrop-blur-sm text-gray-800 rounded-tl-md shadow-md border border-gray-200/50 hover:shadow-lg"
                                                            }`}
                                                    >
                                                        {/* Media file display */}
                                                        {msg.media_file && (
                                                            <div className="mb-3">
                                                                {(() => {
                                                                    const fileExtension = msg.media_file.split('.').pop()?.toLowerCase();
                                                                    const fileName = msg.file_name || msg.media_file.split('/').pop();

                                                                    // Image files
                                                                    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExtension)) {
                                                                        const imgIndex = chatImages.findIndex(img => img.id === msg.id);
                                                                        return (
                                                                            <GalleryComponent
                                                                                images={chatImages}
                                                                                activeIndex={imgIndex !== -1 ? imgIndex : 0}
                                                                                openingComponent={
                                                                                    <div className="relative group cursor-pointer overflow-hidden rounded-lg">
                                                                                        <img
                                                                                            src={msg.media_file}
                                                                                            alt={fileName}
                                                                                            className="rounded-lg max-h-60 w-full object-contain border border-gray-200 transition-transform duration-300 group-hover:scale-105"
                                                                                            onError={(e) => {
                                                                                                e.target.onerror = null;
                                                                                                e.target.src = 'https://via.placeholder.com/150?text=Image+Not+Available';
                                                                                            }}
                                                                                        />
                                                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                                            <div className="bg-white/20 backdrop-blur-md p-2 rounded-full border border-white/30">
                                                                                                <Eye className="w-5 h-5 text-white" />
                                                                                            </div>
                                                                                        </div>
                                                                                        {fileName && (
                                                                                            <div className={`text-xs mt-1 ${msg.sent ? 'text-white/80' : 'text-gray-500'}`}>
                                                                                                {fileName}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                }
                                                                            />
                                                                        );
                                                                    }

                                                                    // Video files
                                                                    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(fileExtension)) {
                                                                        return (
                                                                            <div className="relative">
                                                                                <video
                                                                                    src={msg.media_file}
                                                                                    controls
                                                                                    className="rounded-lg max-h-60 w-full object-contain border border-gray-200"
                                                                                    onError={(e) => {
                                                                                        e.target.onerror = null;
                                                                                        e.target.style.display = 'none';
                                                                                    }}
                                                                                >
                                                                                    Your browser does not support the video tag.
                                                                                </video>
                                                                                {fileName && (
                                                                                    <div className={`text-xs mt-1 ${msg.sent ? 'text-white/80' : 'text-gray-500'}`}>
                                                                                        {fileName}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    }

                                                                    // Audio files
                                                                    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(fileExtension)) {
                                                                        return (
                                                                            <div className="bg-gray-100/50 rounded-lg p-3 border border-gray-200">
                                                                                <audio
                                                                                    src={msg.media_file}
                                                                                    controls
                                                                                    className="w-full"
                                                                                    onError={(e) => {
                                                                                        e.target.onerror = null;
                                                                                        e.target.style.display = 'none';
                                                                                    }}
                                                                                >
                                                                                    Your browser does not support the audio tag.
                                                                                </audio>
                                                                                {fileName && (
                                                                                    <div className={`text-xs mt-2 ${msg.sent ? 'text-white/80' : 'text-gray-500'}`}>
                                                                                        {fileName}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    }

                                                                    // Document files
                                                                    if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(fileExtension)) {
                                                                        return (
                                                                            <div className={`bg-gray-100/50 rounded-lg p-3 border border-gray-200 ${msg.sent ? 'bg-white/20' : 'bg-gray-100/50'}`}>
                                                                                <div className="flex items-center gap-3">
                                                                                    <div className={`p-2 rounded-lg ${msg.sent ? 'bg-white/20' : 'bg-white'}`}>
                                                                                        <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                                                        </svg>
                                                                                    </div>
                                                                                    <div className="flex-1">
                                                                                        <div className={`text-sm font-medium ${msg.sent ? 'text-white' : 'text-gray-800'}`}>
                                                                                            {fileName}
                                                                                        </div>
                                                                                        <div className={`text-xs ${msg.sent ? 'text-white/70' : 'text-gray-500'}`}>
                                                                                            {fileExtension?.toUpperCase()} Document
                                                                                        </div>
                                                                                    </div>
                                                                                    <a
                                                                                        href={msg.media_file}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className={`p-2 rounded-lg ${msg.sent ? 'bg-white/20 hover:bg-white/30' : 'bg-white hover:bg-gray-50'} transition-colors`}
                                                                                    >
                                                                                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                        </svg>
                                                                                    </a>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    // Default file display
                                                                    return (
                                                                        <div className={`bg-gray-100/50 rounded-lg p-3 border border-gray-200 ${msg.sent ? 'bg-white/20' : 'bg-gray-100/50'}`}>
                                                                            <div className="flex items-center gap-3">
                                                                                <div className={`p-2 rounded-lg ${msg.sent ? 'bg-white/20' : 'bg-white'}`}>
                                                                                    <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                                                                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                                    </svg>
                                                                                </div>
                                                                                <div className="flex-1">
                                                                                    <div className={`text-sm font-medium ${msg.sent ? 'text-white' : 'text-gray-800'}`}>
                                                                                        {fileName}
                                                                                    </div>
                                                                                    <div className={`text-xs ${msg.sent ? 'text-white/70' : 'text-gray-500'}`}>
                                                                                        {fileExtension?.toUpperCase()} File
                                                                                    </div>
                                                                                </div>
                                                                                <a
                                                                                    href={msg.media_file}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className={`p-2 rounded-lg ${msg.sent ? 'bg-white/20 hover:bg-white/30' : 'bg-white hover:bg-gray-50'} transition-colors`}
                                                                                >
                                                                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                    </svg>
                                                                                </a>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        )}

                                                        {/* 1. Template messages with template_json */}
                                                        {msg.message_text && msg.template_json ? (
                                                            <div className="flex flex-col">
                                                                {(() => {
                                                                    try {
                                                                        const templateJson = typeof msg.template_json === 'string' ? JSON.parse(msg.template_json) : msg.template_json;
                                                                        return templateJson?.components?.map((component, idx) => {
                                                                            if (component.type === 'HEADER' && component.format === 'IMAGE') {
                                                                                let headerImageUrl = '';
                                                                                if (msg.message_text.includes('image: ')) {
                                                                                    headerImageUrl = msg.message_text.split('image: ')[1];
                                                                                } else if (component.example?.header_handle?.[0]) {
                                                                                    headerImageUrl = component.example.header_handle[0];
                                                                                }
                                                                                if (headerImageUrl) {
                                                                                    const imgIndex = chatImages.findIndex(img => img.id === msg.id);
                                                                                    return (
                                                                                        <div key={idx} className="mb-2">
                                                                                            <GalleryComponent
                                                                                                images={chatImages}
                                                                                                activeIndex={imgIndex !== -1 ? imgIndex : 0}
                                                                                                openingComponent={
                                                                                                    <div className="relative group cursor-pointer overflow-hidden rounded-lg">
                                                                                                        <img
                                                                                                            src={headerImageUrl}
                                                                                                            alt="Template Header"
                                                                                                            className="rounded-lg max-h-24 w-full object-contain border border-gray-200 transition-transform duration-300 group-hover:scale-105"
                                                                                                            onError={(e) => {
                                                                                                                e.target.onerror = null;
                                                                                                                e.target.src = 'https://via.placeholder.com/150?text=Image+Not+Available';
                                                                                                            }}
                                                                                                        />
                                                                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                                                            <div className="bg-white/20 backdrop-blur-md p-2 rounded-full border border-white/30">
                                                                                                                <Eye className="w-5 h-5 text-white" />
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                            }
                                                                            if (component.type === 'BODY') {
                                                                                return (
                                                                                    <div key={idx} className={`mt-1 text-sm break-words whitespace-pre-wrap leading-relaxed ${msg.sent ? 'text-white' : 'text-gray-800'}`}
                                                                                        dangerouslySetInnerHTML={{ __html: formatMessageText(component.text) }}
                                                                                    />
                                                                                );
                                                                            }
                                                                            if (component.type === 'FOOTER') {
                                                                                return (
                                                                                    <div key={idx} className={`text-xs mt-1 font-medium ${msg.sent ? 'text-white/70' : 'text-gray-500'}`}>
                                                                                        {component.text}
                                                                                    </div>
                                                                                );
                                                                            }
                                                                            if (component.type === 'BUTTONS') {
                                                                                return (
                                                                                    <div key={idx} className="flex flex-col gap-1 mt-2 pt-2 border-t border-gray-200/30">
                                                                                        {component.buttons?.map((btn, btnIdx) => {
                                                                                            const isUrl = btn.type === 'URL';
                                                                                            const isPhone = btn.type === 'PHONE_NUMBER';
                                                                                            const href = isUrl && btn.url ? btn.url : isPhone && btn.phone_number ? `tel:${btn.phone_number}` : '#';

                                                                                            return (
                                                                                                <a
                                                                                                    key={btnIdx}
                                                                                                    href={href}
                                                                                                    target={isUrl ? "_blank" : undefined}
                                                                                                    rel={isUrl ? "noopener noreferrer" : undefined}
                                                                                                    className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-semibold transition-colors ${msg.sent ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20' : 'bg-white hover:bg-gray-50 text-[#00a884] border border-gray-200 shadow-sm'}`}
                                                                                                >
                                                                                                    {isUrl && (
                                                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                                                                    )}
                                                                                                    {isPhone && (
                                                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                                                                                    )}
                                                                                                    {btn.text}
                                                                                                </a>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        });
                                                                    } catch (e) {
                                                                        console.error("Error parsing template JSON", e);
                                                                        return null;
                                                                    }
                                                                })()}
                                                            </div>
                                                        ) : msg.message_text?.includes('Template') &&
                                                            msg.message_text.includes('image:') ? (
                                                            <div className="mb-2">
                                                                <div className="text-xs text-gray-500 mb-1">
                                                                    {msg.message_text.split(' - ')[0]}
                                                                </div>
                                                                {(() => {
                                                                    const imageUrl = msg.message_text.split('image: ')[1];
                                                                    const imgIndex = chatImages.findIndex(img => img.id === msg.id);
                                                                    return (
                                                                        <GalleryComponent
                                                                            images={chatImages}
                                                                            activeIndex={imgIndex !== -1 ? imgIndex : 0}
                                                                            openingComponent={
                                                                                <div className="relative group cursor-pointer overflow-hidden rounded-lg">
                                                                                    <img
                                                                                        src={imageUrl}
                                                                                        alt="Template"
                                                                                        className="rounded-lg max-h-60 w-full object-contain border border-gray-200 transition-transform duration-300 group-hover:scale-105"
                                                                                        onError={(e) => {
                                                                                            e.target.onerror = null;
                                                                                            e.target.src = 'https://via.placeholder.com/150?text=Image+Not+Available';
                                                                                        }}
                                                                                    />
                                                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                                        <div className="bg-white/20 backdrop-blur-md p-2 rounded-full border border-white/30">
                                                                                            <Eye className="w-5 h-5 text-white" />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            }
                                                                        />
                                                                    );
                                                                })()}
                                                            </div>
                                                        ) :
                                                            youtubeId ? (
                                                                <div className="mb-2 w-full max-w-[560px]">
                                                                    <div className="relative pt-[56.25%] rounded-lg overflow-hidden bg-black">
                                                                        <iframe
                                                                            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=0`}
                                                                            className="absolute top-0 left-0 w-full h-full"
                                                                            frameBorder="0"
                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                            allowFullScreen
                                                                        />
                                                                    </div>
                                                                    <a
                                                                        href={youtubeUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-xs text-gray-500 mt-1 inline-block hover:underline"
                                                                    >
                                                                        Watch on YouTube
                                                                    </a>
                                                                </div>
                                                            ) :

                                                                /* 3. Regular URLs (non-YouTube) */
                                                                msg.message_text?.match(/https?:\/\/[^\s]+/) ? (
                                                                    <div className="mb-2">
                                                                        <a
                                                                            href={msg.message_text}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-blue-500 hover:underline break-all"
                                                                        >
                                                                            {msg.message_text}
                                                                        </a>
                                                                    </div>
                                                                ) :

                                                                    /* 4. Regular text messages */
                                                                    msg.message_text && (
                                                                        <div
                                                                            className={`break-words whitespace-pre-wrap leading-relaxed ${msg.sent ? 'text-white' : 'text-gray-800'}`}
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: formatMessageText(msg.message_text)
                                                                            }}
                                                                        />
                                                                    )}

                                                        {/* Message timestamp and status */}
                                                        <div className="flex justify-end items-center mt-2 gap-1 text-xs">
                                                            <span className={`font-medium ${msg.sent ? 'text-white/80' : 'text-gray-500'}`}>
                                                                {formatDateTime(msg.created_at).split(' ')[1]}
                                                            </span>
                                                            {msg.sent && (msg.status === "read" ? (
                                                                <BiCheckDouble className="text-blue-200" size={16} />
                                                            ) : msg.status === "received" ? (
                                                                <BiCheckDouble className="text-white/60" size={16} />
                                                            ) : (
                                                                <BiCheck className="text-white/60" size={16} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        )}

                        {/* File Input (Hidden) */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={handleFileInputChange}
                            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.rtf"
                        />

                        {/* File Preview */}
                        {selectedFile && (
                            <div className="px-4 py-2 bg-gray-50/80 border-t border-gray-200/60">
                                <div className="flex items-center gap-3 p-3 bg-white/90 rounded-xl border border-gray-200/50 shadow-sm">
                                    <div className="flex-shrink-0 p-2 bg-green-100 rounded-lg">
                                        {getFileIcon(selectedFile.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-800 truncate">
                                            {selectedFile.name}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {formatFileSize(selectedFile.size)}
                                        </div>
                                    </div>
                                    <button
                                        onClick={removeSelectedFile}
                                        className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Message Input */}
                        <div className="p-4 bg-gradient-to-r from-white to-gray-50/50 border-t border-gray-200/60 backdrop-blur-sm">
                            <div className="flex items-end gap-3">
                                <button
                                    className="p-3 text-gray-500 hover:text-green-600 rounded-full hover:bg-green-50/80 transition-colors duration-200"
                                    onClick={handleToggleEmogies}
                                >
                                    <BsFillEmojiGrinFill size={22} />
                                </button>
                                <button
                                    className="p-3 text-gray-500 hover:text-green-600 rounded-full hover:bg-green-50/80 transition-colors duration-200"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Paperclip size={22} />
                                </button>
                                <div className="flex-1 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-2xl px-4 py-3 shadow-sm hover:shadow-md transition-all duration-200 focus-within:ring-2 focus-within:ring-green-500/30 focus-within:border-green-300">
                                    <textarea
                                        ref={textareaRef}
                                        className="w-full bg-transparent border-none focus:outline-none resize-none max-h-32 text-gray-800 placeholder-gray-400 leading-relaxed"
                                        value={message}
                                        onChange={(e) => {
                                            setMessage(e.target.value);
                                            setIsTyping(true);
                                        }}
                                        placeholder="Type a message..."
                                        rows={1}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                sendMessage();
                                            }
                                        }}
                                    />
                                </div>
                                <button
                                    className={`group p-3 rounded-full transition-all duration-300 ${(message.trim() || selectedFile)
                                        ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl active:scale-95'
                                        : 'bg-gray-200/80 text-gray-400 cursor-not-allowed'
                                        }`}
                                    onClick={sendMessage}
                                    disabled={(!message.trim() && !selectedFile) || loading}
                                >
                                    {loading ? (
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" width="20" height="20" className="rotate-0 transition-transform duration-200 group-hover:rotate-12">
                                            <path fill="currentColor" d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="hidden md:flex flex-col items-center justify-center h-full bg-gray-50">
                        <div className="text-center p-6 max-w-md">
                            <BsWhatsapp size={64} className="mx-auto text-green-500 mb-4" />
                            <h3 className="text-2xl font-light text-gray-700 mb-2">WhatsApp Web</h3>
                            <p className="text-gray-500 mb-6">
                                Send and receive messages without keeping your phone online.
                                <br />
                                Use WhatsApp on up to 4 linked devices and 1 phone at the same time.
                            </p>
                            <p className="text-sm text-gray-400">
                                Select a chat to start messaging
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Emoji Picker Modal */}
            <ShowModal
                componentToPass={<EmojiPicker onEmojiClick={onEmogiesChange} width="100%" height={400} />}
                contentLabel="Emoji Picker"
                isOpen={showEmogies}
                modalWidth="350px"
                onRequestClose={handleToggleEmogies}
            />

            {/* Export Messages Modal */}
            {showExportModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowExportModal(false); }}
                >
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" style={{ animation: 'slideUp 0.25s ease-out' }}>

                        {/* Modal Header */}
                        <div className="relative p-6 bg-gradient-to-br from-green-500 via-green-500 to-emerald-600 overflow-hidden">
                            <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
                            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-white/10 rounded-full" />
                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
                                        <Download size={22} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-bold text-xl">Export Messages</h3>
                                        <p className="text-green-100 text-xs mt-0.5">Download as Excel spreadsheet</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowExportModal(false)}
                                    className="p-2 rounded-full bg-white/15 hover:bg-white/30 transition-all text-white border border-white/20"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-5">

                            {/* Quick Presets */}
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Quick Select</p>
                                <div className="grid grid-cols-4 gap-2">
                                    {[
                                        { id: 'today', label: 'Today' },
                                        { id: 'yesterday', label: 'Yesterday' },
                                        { id: 'last7', label: 'Last 7 Days' },
                                        { id: 'thisMonth', label: 'This Month' },
                                    ].map(({ id, label }) => (
                                        <button
                                            key={id}
                                            onClick={() => applyPreset(id)}
                                            className={`py-2 px-2 rounded-xl text-xs font-semibold border transition-all duration-150 ${
                                                activePreset === id
                                                    ? 'bg-green-500 text-white border-green-500 shadow-md shadow-green-200 scale-[1.04]'
                                                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-green-400 hover:text-green-600 hover:bg-green-50'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-gray-200" />
                                <span className="text-xs text-gray-400 font-medium">or set custom range</span>
                                <div className="flex-1 h-px bg-gray-200" />
                            </div>

                            {/* Date Range Cards */}
                            <div className="grid grid-cols-2 gap-3">
                                {/* Start */}
                                <div className="space-y-2">
                                    <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
                                        From
                                    </p>
                                    <div className="relative">
                                        <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        <input
                                            type="date"
                                            className="w-full pl-8 pr-2 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400/40 focus:border-green-400 transition-all bg-gray-50 hover:bg-white text-gray-700"
                                            value={exportForm.startDate}
                                            onChange={(e) => {
                                                setActivePreset(null);
                                                setExportForm(p => ({ ...p, startDate: e.target.value }));
                                            }}
                                        />
                                    </div>
                                    <ClockTimePicker
                                        value={exportForm.startTime}
                                        onChange={(val) => setExportForm(p => ({ ...p, startTime: val }))}
                                    />
                                </div>

                                {/* End */}
                                <div className="space-y-2">
                                    <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
                                        To
                                    </p>
                                    <div className="relative">
                                        <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        <input
                                            type="date"
                                            className="w-full pl-8 pr-2 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400/40 focus:border-green-400 transition-all bg-gray-50 hover:bg-white text-gray-700"
                                            value={exportForm.endDate}
                                            onChange={(e) => {
                                                setActivePreset(null);
                                                setExportForm(p => ({ ...p, endDate: e.target.value }));
                                            }}
                                        />
                                    </div>
                                    <ClockTimePicker
                                        value={exportForm.endTime}
                                        onChange={(val) => setExportForm(p => ({ ...p, endTime: val }))}
                                    />
                                </div>
                            </div>

                            {/* Selected range summary */}
                            {(exportForm.startDate || exportForm.endDate) && (
                                <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 rounded-xl border border-green-100">
                                    <Calendar size={14} className="text-green-600 flex-shrink-0" />
                                    <p className="text-xs text-green-700 font-medium">
                                        {exportForm.startDate ? `${exportForm.startDate} ${exportForm.startTime}` : '—'}
                                        <span className="mx-2 text-green-400">→</span>
                                        {exportForm.endDate ? `${exportForm.endDate} ${exportForm.endTime}` : '—'}
                                    </p>
                                </div>
                            )}

                            {/* Keyword */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <Search size={12} />
                                    Message Keyword
                                    <span className="font-normal normal-case tracking-normal text-gray-400 ml-1">(optional)</span>
                                </label>
                                <div className="relative mt-2">
                                    <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        type="text"
                                        placeholder="मैं 12 टीथ सीडर के बारे में..."
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400/40 focus:border-green-400 transition-all bg-gray-50 hover:bg-white placeholder-gray-400"
                                        value={exportForm.message_keyword}
                                        onChange={(e) => setExportForm(p => ({ ...p, message_keyword: e.target.value }))}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 pb-6 flex gap-3">
                            <button
                                onClick={() => { setShowExportModal(false); setActivePreset(null); }}
                                className="flex-1 py-3 px-4 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleExportMessages}
                                disabled={exportLoading || !exportForm.startDate || !exportForm.endDate}
                                className={`flex-1 py-3 px-4 rounded-2xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all ${
                                    exportLoading || !exportForm.startDate || !exportForm.endDate
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg shadow-green-200 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                                }`}
                            >
                                {exportLoading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Exporting...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} />
                                        Export &amp; Download
                                    </>
                                )}
                            </button>
                        </div>

                        <style>{`
                            @keyframes slideUp {
                                from { opacity: 0; transform: translateY(20px) scale(0.97); }
                                to   { opacity: 1; transform: translateY(0)    scale(1); }
                            }
                        `}</style>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Whatsapp;